/**
 * pages/KontakPage.js
 * Master/detail contacts page. Full-width list only until a contact is selected (no empty
 * placeholder pane) — selecting one switches to a two-pane layout: the SAME list, narrowed
 * to the left column, plus a detail pane (right) with a "← Kembali" back to full-width, an
 * info card, and the contact's Riwayat Transaksi (transaction history).
 *
 * Contact model redesign, KontakPage rebuild (batch 2b): PUSAT and CABANG are two separate
 * lists that know nothing about each other (`Contact.list` is fixed at creation — see
 * utils/contacts.js) — this page now shows them as two tabs, mirroring the location toggle
 * Penjualan already uses. Search/sort/the A-Z index all operate within the active tab only;
 * the header count reflects the active tab only. An internal contact's name is ALWAYS resolved
 * via `displayContact`, never its raw `.name` placeholder — this applies to display, search,
 * sort, and the A-Z index alike (see `nameOf` below). The detail pane now reads the real
 * Contact shape (`addresses[]`, `ekspedisi`, `keterangan`, `npwp`, `pkp`, `labels`,
 * `personIds`) instead of the old, no-longer-existing `.address`/`.notes` fields.
 *
 * Batch 2c (done): the old Riwayat Stok section (item-creation/adjustment history, filtered by
 * `history[].contactId`) is REMOVED from this page — it predated transactions and only ever
 * showed PEMBUATAN/PENYESUAIAN entries, never a sale/purchase/transfer, so it always read empty
 * for an internal (branch) contact even though that's exactly the case staff need to see. It's
 * replaced by "Riwayat Transaksi", a read-only list of `data.transactions` referencing this
 * contact (`t.contactId === selected.id`), date-filtered, row click opens the existing
 * `TransactionDetailModal` (no new detail UI). `history[].contactId` itself is UNCHANGED — it
 * still drives `contactRefCount`'s delete guard below, only this page's OWN display of it is
 * gone (the old `entriesByContact`/`selectedEntries` map is deleted since nothing on this page
 * reads it anymore). Batch 2d adds business linking — not touched here.
 *
 * Batch 2c-fix (Lokasi column + branch filter): every row now shows a Lokasi chip (real branch
 * name for a Cabang-anchored transaction, "Pusat" otherwise — the SAME expression Penjualan.js's
 * own LOKASI badge uses, resolved live via `resolveBranchName`, never a stored name), on every
 * contact, unconditionally. This is what actually answers the internal-contact asymmetry
 * documented below (and in App.js's `resolveTransferContact`/the root CLAUDE.md): the single
 * CABANG-list "Pusat" stand-in mixes every branch's transfers, and the Lokasi column is what
 * lets a reader tell them apart row by row. On TOP of that, when a selected INTERNAL contact's
 * own transactions actually span more than one branch (`showBranchFilter` below — true only for
 * that one shared "Pusat" stand-in, never for a PUSAT-list per-branch stand-in, which only ever
 * has one branch in its own history), a branch filter appears alongside the date filter so
 * "what did MMC send us this month" is one selection, not a manual scan. Deliberately NEVER
 * shown for a normal (non-internal) contact, even though nothing in the data model strictly
 * guarantees a normal Cabang-list contact's own transactions all share one branch (see this
 * file's own `showBranchFilter` comment for the caveat) — a normal contact simply never gets
 * this control, per instruction.
 *
 * **Section F, batch 4 (buyer-side transfers)**: a real gap found by investigation, not just an
 * asymmetry to document — `transactionsByContact`'s plain `contactId` lookup only ever showed a
 * transfer on the SELLER's own contact (per App.js's `resolveTransferContact`, `contactId` is
 * always filed under the source/seller side); the BUYER's own internal contact showed nothing at
 * all for that same transfer. Fixed via `internalContactTxns` (above `sortTxnsDesc`) — an
 * internal contact's Riwayat Transaksi is now collected by what it REPRESENTS (every transfer
 * touching its own branch, in either direction, for a PUSAT-list stand-in; every transfer, any
 * branch, any direction, for the single CABANG-list "Pusat" stand-in) instead of by `contactId`.
 * `internalPerspectiveFor` (same location) threads the correct side into `txnStatus`/
 * `transferStatusFor` so each contact's own page reads PIUTANG/UTANG from ITS OWN side, never the
 * old always-PIUTANG whole-business default. The existing branch filter's own meaning is
 * unchanged — it still narrows to one Cabang branch's transfers — only the underlying set it
 * narrows got wider (both directions instead of one). A normal contact is untouched: it still
 * reads straight off `transactionsByContact`, unaffected by any of this.
 *
 * **Section F, batch 5 (transfer direction, visible)**: batch 4 (above) means a single internal
 * contact's Riwayat Transaksi can now legitimately mix `PUSAT_TO_CABANG` and `CABANG_TO_PUSAT`
 * rows — with no visible clue which is which beyond the Lokasi badge, which only answers that if
 * the reader already knows it names the SOURCE. `renderTxnRow`'s Jenis cell now grows a muted
 * sub-line, ONLY for a TRANSFER row, showing `transferDirectionLabel(t, branches)`
 * (`utils/piutangUtang.js`) — the exact "{source} → {destination}" string Piutang & Utang's own
 * Internal tab already renders on its Pihak cell, reused verbatim rather than a second phrasing.
 * Folded into the existing Jenis column (which already says "Transfer" on these rows) rather than
 * a new column, so the table's own width is unaffected for every other contact, where a TRANSFER
 * row never appears at all. A PENJUALAN/PEMBELIAN row renders byte-identical to before this batch.
 */
import React, { useMemo, useState } from "react";
import { today, fmtDateShort, fmtQty } from "../utils/idGenerators";
import { displayContact, contactRefCount } from "../utils/contacts";
import { contactsForBusiness, displayBusiness } from "../utils/businesses";
import { suggestBusinessMatches } from "../utils/contactMatch";
import { resolvePersonName, resolvePersonCode } from "../utils/persons";
import { txnStatus, dueState, TXN_DUE_PRESETS, dueWithinMatches, dueWithinActiveN as resolveDueWithinActiveN, dueWithinSummaryLabel as resolveDueWithinSummaryLabel } from "../utils/txnStatus";
import { outstanding, outstandingForContact, outstandingForBusiness, transferDirectionLabel } from "../utils/piutangUtang";
import { resolveBranchName } from "../utils/branches";
import { bucketLabel } from "../utils/transformations";
import { downloadContactExport } from "../utils/contactExport";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import ContactFormModal from "../components/ContactFormModal";
import BusinessLinkModal from "../components/BusinessLinkModal";
import TransactionDetailModal from "../components/TransactionDetailModal";
import TxnNilaiCell from "../components/TxnNilaiCell";
import { StatusPill, DueBadge } from "../components/TxnBadges";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const LABEL_TEXT = { supplier: "Supplier", customer: "Pelanggan", broker: "Broker" };
const TXN_TYPE_LABEL = { PENJUALAN: "Penjualan", PEMBELIAN: "Pembelian", TRANSFER: "Transfer" };
const CONTACT_LIST_LABEL = { PUSAT: "Pusat", CABANG: "Cabang" };
/** "Nett Piutang" / "Nett Utang" / "Nett" (NOL) — the one label→direction mapping for an
 *  `outstanding()` result's `nettArah`, so the Bisnis card's two tiles (self + firm) can never
 *  word this differently from each other. */
const nettLabel = (o) => (o.nettArah === "PIUTANG" ? "Nett Piutang" : o.nettArah === "UTANG" ? "Nett Utang" : "Nett");

// ── Riwayat Transaksi date-range filter (batch 2c) ──────────────────────────────────────────
// Transaction.date is already a plain "YYYY-MM-DD" string (utils/idGenerators's `today()`), NOT
// an ISO timestamp — unlike Transformation.date, which IS a full ISO timestamp and is why
// RiwayatTransformasi.js needs its own `localDateStr` re-parse to avoid a timezone shift. This
// filter deliberately does NOT copy `localDateStr`: re-parsing an already-local date-only string
// through `new Date(...)` would parse it as UTC MIDNIGHT, and reading that back with local
// getters would shift the day backward for any timezone west of UTC — introducing the exact bug
// class `localDateStr` exists to avoid, not reusing its fix. Don't "fix" this by making the two
// filters match — they differ because the underlying fields differ.
//
// All boundary math below (week/month starts) is done on LOCAL date parts, never UTC or
// `toLocaleDateString` (see the root CLAUDE.md's fmtDateShort/fmtDateLong note for why) — a
// "YYYY-MM-DD" string is parsed via `new Date(s + "T00:00:00")` (forces local-time parsing,
// since a date-only string with no time/offset parses as UTC) and re-serialized by hand.
const fmtLocalDate = (d) => [
  d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0"),
].join("-");
const parseLocalDateStr = (s) => new Date(s + "T00:00:00");
// Week starts Monday (ISO/Indonesian convention), not Sunday.
const startOfWeek = (dateStr) => {
  const d = parseLocalDateStr(dateStr);
  const day = d.getDay(); // 0=Sun..6=Sat
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return fmtLocalDate(d);
};
const endOfWeek = (dateStr) => {
  const d = parseLocalDateStr(startOfWeek(dateStr));
  d.setDate(d.getDate() + 6);
  return fmtLocalDate(d);
};
const startOfMonth = (dateStr) => {
  const d = parseLocalDateStr(dateStr);
  return fmtLocalDate(new Date(d.getFullYear(), d.getMonth(), 1));
};
const endOfMonth = (dateStr) => {
  const d = parseLocalDateStr(dateStr);
  return fmtLocalDate(new Date(d.getFullYear(), d.getMonth() + 1, 0)); // day 0 = last day of this month
};
const startOfLastMonth = (dateStr) => {
  const d = parseLocalDateStr(dateStr);
  return fmtLocalDate(new Date(d.getFullYear(), d.getMonth() - 1, 1));
};
const endOfLastMonth = (dateStr) => {
  const d = parseLocalDateStr(dateStr);
  return fmtLocalDate(new Date(d.getFullYear(), d.getMonth(), 0)); // day 0 of THIS month = last day of previous
};

const TXN_DATE_PRESETS = [
  ["hari_ini", "Hari Ini"],
  ["minggu_ini", "Minggu Ini"],
  ["bulan_ini", "Bulan Ini"],
  ["bulan_lalu", "Bulan Lalu"],
  ["semua", "Semua"],
];
const TXN_PRESET_LABEL = Object.fromEntries(TXN_DATE_PRESETS);

/** Preset key -> [fromDate, toDate] ("" / "" for "semua", meaning no bound). */
function txnPresetRange(preset, t) {
  if (preset === "hari_ini") return [t, t];
  if (preset === "minggu_ini") return [startOfWeek(t), endOfWeek(t)];
  if (preset === "bulan_ini") return [startOfMonth(t), endOfMonth(t)];
  if (preset === "bulan_lalu") return [startOfLastMonth(t), endOfLastMonth(t)];
  return ["", ""];
}

// ── Riwayat Transaksi due-within filter (batch 2c-due) ──────────────────────────────────────
// Deliberately the mirror image of TXN_DATE_PRESETS above, not a variant of it: that filter asks
// "when did this transaction HAPPEN" (t.date, backward-looking, can reach into the past via
// "Bulan Lalu"/"Semua"); this one asks "when is it DUE" (dueState(t, today()), forward-looking
// ONLY — "N Hari Lagi" ("N more days"), never "N Hari Lalu"). Button text carries the direction
// itself (not just a row caption) specifically so the two `.seg` rows can never be mistaken for
// two copies of the same control even skimmed quickly. "semua" here means "don't filter by due
// date at all" (every row passes, including one with no dueDate) — NOT "every row has a due date
// within some range." Deliberately EXCLUDES 'overdue' — overdue is a current-problem state
// surfaced unconditionally via the always-visible banner further down, not a "will become due
// soon" state this forward-only filter is asking about.
// **The bucket decision itself (`TXN_DUE_PRESETS`, `dueWithinMatches`, `dueWithinActiveN`,
// `dueWithinSummaryLabel`) now lives in `utils/txnStatus.js`, imported above — extracted (Bisnis
// page work, batch 3) so the Bisnis page's own horizon filter can reuse the IDENTICAL logic
// instead of a second, copy-pasted one. This page no longer defines any of it locally; don't
// reintroduce a page-local copy here.**

// ── Internal contacts: buyer-side + seller-side transfers (section F, batch 4) ─────────────
// A transfer's `contactId` (App.js's `resolveTransferContact`) is always filed under the
// SOURCE/seller side's own contact — a PUSAT_TO_CABANG transfer stamps the PUSAT-list stand-in
// for its destination branch; a CABANG_TO_PUSAT transfer stamps the single CABANG-list "Pusat"
// stand-in. Reading a contact's history by `contactId` alone (the old `transactionsByContact`
// lookup, still used for every NORMAL contact below) therefore only ever shows a transfer on
// the SELLER's own page — the buyer's internal contact showed nothing at all. Fixed here by
// collecting an internal contact's transfers by what it REPRESENTS instead of by `contactId`:
// in BOTH directions `t.branchId` always names the one real Cabang branch a transfer touches
// (the destination for PUSAT_TO_CABANG, the source for CABANG_TO_PUSAT — confirmed against
// App.js's `createTransaction`), so "every transfer touching branch B" is simply "every
// TRANSFER with `branchId === B`," regardless of direction. This is a STRICT SUPERSET of the
// old contactId-only set for a PUSAT-list stand-in (adds the CABANG_TO_PUSAT transfers FROM its
// branch); for the single CABANG-list "Pusat" stand-in it's just "every TRANSFER, full stop" —
// every transfer touches Pusat on one end, so there's nothing to filter by branch at this level
// (the existing branch filter below narrows the DISPLAYED rows, it never changes what's
// collected here). A transaction can therefore now legitimately appear on TWO DIFFERENT
// contacts' own pages (the branch's own PUSAT-list stand-in AND the shared CABANG-list "Pusat"
// stand-in) — that's the fix working as intended, not a duplicate; it can never appear TWICE on
// the SAME contact's own page, since each collector here is one plain `filter`, never a union of
// two queries that could overlap. Never reads `contactId` for an internal contact at all.
function internalContactTxns(transactions, contact) {
  if (contact.list === "CABANG") return transactions.filter((t) => t.type === "TRANSFER");
  return transactions.filter((t) => t.type === "TRANSFER" && t.branchId === contact.internalBranchId);
}

// The perspective `txnStatus`/`transferStatusFor` needs to answer PIUTANG/UTANG correctly for a
// row collected above — never a second direction-deciding mechanism, just "which side's books is
// THIS internal contact's page standing in for." A PUSAT-list stand-in is always Pusat's own
// books (`null`), regardless of which branch it's named after — confirmed against the worked
// example (a Pusat→TMM transfer reads PIUTANG on the Pusat-list "TMM" contact, exactly what
// `transferStatusFor(txn, null)` returns for a PUSAT_TO_CABANG row). The single CABANG-list
// "Pusat" stand-in takes the ROW's OWN `branchId` — always defined for a TRANSFER, and always
// equal to `txn.branchId` by construction, so `transferStatusFor`'s own branch-mismatch guard can
// never fire here. Returns `undefined` (txnStatus's own "no perspective" default) for anything
// that isn't an internal contact looking at a TRANSFER — a normal contact's PENJUALAN/PEMBELIAN
// rows are perspective-independent and never reach this at all.
function internalPerspectiveFor(contact, t) {
  if (!contact?.internal || t.type !== "TRANSFER") return undefined;
  return contact.list === "PUSAT" ? null : t.branchId;
}

/** Newest-first: transactions (unlike history entries) have no compensating-reversal-entry tie
 *  to worry about — a plain date/time/createdAt sort is enough, no `sortHistoryDesc`-style
 *  secondary id key needed. */
function sortTxnsDesc(list) {
  return [...list].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? 1 : -1;
    if (a.time !== b.time) return a.time < b.time ? 1 : -1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
}

// Bisnis page work (batch 2): `jumpTo` is a one-shot `{contactId, list} | null` from a Bisnis-page
// member row click (App.js's own `kontakJumpTo`), consumed ONLY as the lazy initial state for
// `tab`/`selectedId` below — this page always fully unmounts/remounts on the way in (App.js
// renders it as a plain `{page === "kontak" && (...)}` block), so there is no later render where
// a stale `jumpTo` could re-apply; nothing here reads `jumpTo` again after mount.
export default function KontakPage({ contacts = [], items = [], history = [], transactions = [], persons = [], salespeople = [], branches = [], businesses = [], onAdd, onUpdate, onDelete, onLinkContacts, onImportKontak, onOpenBisnis, jumpTo = null }) {
  const [tab, setTab] = useState(() => jumpTo?.list || "PUSAT"); // "PUSAT" | "CABANG" — the two lists never merge
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("az"); // "az" | "za"
  const [letter, setLetter] = useState(null); // null ("Semua") | "A".."Z"
  const [hideDeactivated, setHideDeactivated] = useState(false);
  const [selectedId, setSelectedId] = useState(() => jumpTo?.contactId || null);
  const [formModal, setFormModal] = useState(null); // null | { initial: contact|null }
  const [saveError, setSaveError] = useState(null); // reason the open form's last save attempt failed, or null
  const [deleteTarget, setDeleteTarget] = useState(null); // contact | null
  const [detailTxnId, setDetailTxnId] = useState(null); // transaction id shown in TransactionDetailModal, or null
  // Business linking (batch 2d) — see BusinessLinkModal.js and this page's own "Bisnis" card.
  const [linkModal, setLinkModal] = useState(null); // null | { contact, preselectContactIds, preselectBusinessIds }
  const [linkError, setLinkError] = useState(null); // reason the open link modal's last confirm failed, or null
  // Suggested-match banner (batch 2d) — set only right after a BY-HAND create finds at least one
  // candidate (suggestBusinessMatches); "if nothing is found, show NOTHING" (task spec) is simply
  // never setting this at all. A card/banner the user must act on or dismiss, never a fading toast.
  const [suggestFor, setSuggestFor] = useState(null); // null | { contact, matches }
  // Riwayat Transaksi date filter — default "Bulan Ini" (this company's own reporting/accounting
  // cadence is monthly, see pages/CLAUDE.md's KontakPage entry). A contact can have years of
  // history, so the active range is always shown plainly (txnRangeLabel below) — it must never
  // look like "this is everything" by default.
  const [txnPreset, setTxnPreset] = useState("bulan_ini");
  const [[txnFromDate, txnToDate], setTxnRange] = useState(() => txnPresetRange("bulan_ini", today()));
  const chooseTxnPreset = (preset) => { setTxnRange(txnPresetRange(preset, today())); setTxnPreset(preset); };
  // Riwayat Transaksi branch filter — "SEMUA" (all branches) by default. Only ever rendered/
  // meaningful for the one internal contact whose OWN transactions actually span more than one
  // branch (see `showBranchFilter` below); left at "SEMUA" and simply unused otherwise, so no
  // reset-on-contact-switch effect is needed — same "just sits there unused" precedent the date
  // filter's own state already follows across a contact switch.
  const [txnBranchFilter, setTxnBranchFilter] = useState("SEMUA");
  // Outstanding-only toggle and the due-within filter (batch 2c-due) — both OFF by default (the
  // table's job is still to show history, not just what's owed), both fully independent of every
  // other Riwayat Transaksi filter and of each other: setting one never flips another. Neither is
  // reset on a contact switch, same "just sits there, harmless" precedent txnBranchFilter already
  // follows above.
  const [txnOutstandingOnly, setTxnOutstandingOnly] = useState(false);
  const [txnDueWithin, setTxnDueWithin] = useState("semua"); // "semua" | "7" | "30" | "kustom"
  // Raw text for the "Kustom" day-count field (batch 2c-due-fix) — a RAW STRING, not coerced on
  // every keystroke, same "stuck-zero" avoidance `TransactionFormModal.js`'s own `dueDaysText`
  // already documents: coercing directly would snap a cleared field to "0" and break further
  // typing. Only meaningful while `txnDueWithin === "kustom"`; harmless and simply unused
  // otherwise (same "just sits there" precedent as the rest of this filter's own state), so it's
  // never cleared just because the user switches to a different preset — picking "Kustom" again
  // later remembers what was last typed.
  const [txnDueCustomText, setTxnDueCustomText] = useState("");
  // The always-visible overdue-total disclosure (below) — collapsed by default; toggled purely by
  // the user clicking the banner, never derived from any filter.
  const [showOverdueBlock, setShowOverdueBlock] = useState(false);

  // Switching tabs leaves the detail pane and letter filter behind — a contact from the OTHER
  // list is never a meaningful "selected row" or "active letter" once the visible list changes
  // out from under it. The search text (q) deliberately survives the switch — comparing the
  // same typed name across both lists is a real use (the same real firm can have one entry in
  // each list, see the edge case in this batch's own task notes).
  const changeTab = (next) => {
    if (next === tab) return;
    setTab(next);
    setSelectedId(null);
    setLetter(null);
  };

  // Jump to a specific contact from the "Bisnis" card's other-entries list (batch 2d) — the ONE
  // deliberate exception to `changeTab`'s own "switching tabs always clears the selection" rule
  // above. That rule stays exactly as it was for the Pusat/Cabang toggle buttons (a manual tab
  // switch has no meaningful destination contact yet, so clearing is correct there); this is a
  // SEPARATE function, not a change to `changeTab` itself, used only when the destination is
  // already known — it switches the tab (only if actually needed) AND selects the target in the
  // same action, so clicking another entry in the same firm never lands on a blank pane the user
  // would have to reselect from. `letter` still resets (same reasoning `changeTab` already has:
  // a letter from the tab just left isn't meaningful against the new one); `q` is left alone,
  // same as `changeTab`.
  const jumpToContact = (c) => {
    if (c.list !== tab) { setTab(c.list); setLetter(null); }
    setSelectedId(c.id);
  };

  // ONE contactId -> transactions[] map built once over the full transactions collection —
  // never a per-contact filter inside the list render. Mirrors the old `entriesByContact` map's
  // own performance discipline (batch 2b), now over `transactions` instead of `history` — that
  // map is deleted (batch 2c), since nothing on this page reads history-by-contact any more.
  // **Still the ONLY source for a NORMAL contact's own transactions** — section F batch 4's
  // buyer+seller fix (`internalContactTxns`, above) applies ONLY to an internal contact; a normal
  // contact's `contactId` already names the one real relationship it has, so there's no "other
  // side" to also collect.
  const transactionsByContact = useMemo(() => {
    const m = new Map();
    for (const t of transactions) {
      if (!t.contactId) continue;
      const arr = m.get(t.contactId);
      if (arr) arr.push(t); else m.set(t.contactId, [t]);
    }
    return m;
  }, [transactions]);

  // Internal contacts' own EXPANDED transaction count (buyer + seller, `internalContactTxns`
  // above) — built once via `useMemo`, same "never filter inside the render loop" discipline as
  // `transactionsByContact` above, so the browsing list's own "N transaksi" count can never
  // disagree with what the detail pane actually shows once opened (section F batch 4 fix — the
  // list row used to read the narrower contactId-only count via `transactionsByContact`, which
  // would now under-report against the widened detail view). Only ever consulted for `c.internal`
  // rows; a normal contact keeps reading its count straight off `transactionsByContact`.
  const internalTxnCounts = useMemo(() => {
    const m = new Map();
    for (const c of contacts) {
      if (c.internal) m.set(c.id, internalContactTxns(transactions, c).length);
    }
    return m;
  }, [contacts, transactions]);

  // An internal contact's own `.name` is a fixed, never-real placeholder ("__internal__") — no
  // display path here may read it directly (list row, search, sort, A-Z index, detail header).
  // Always resolved live via `displayContact`: the CABANG stand-in always shows "Pusat"; a PUSAT
  // stand-in resolves its branch's CURRENT name, or "—" (never a fabricated sentence) when that
  // branch can't be resolved (edge case: a branch deleted out from under its own internal
  // contact's stale `internalBranchId`). A normal contact's own `.name` is read directly, same
  // as before — city stays a separate field so the list row can style it as its own muted span.
  const nameOf = (c) => (c.internal ? (displayContact(c, branches) || "—") : (c.name || ""));

  const tabContacts = useMemo(() => contacts.filter((c) => c.list === tab), [contacts, tab]);

  // Letters with at least one contact — computed against the active TAB's full list (not the
  // current search or the deactivated-hide toggle), same as a phone-book index: it's a
  // navigation aid independent of any transient refinement, scoped only by the structural
  // "which list am I browsing" choice (the tab).
  const availableLetters = useMemo(() => {
    const s = new Set();
    for (const c of tabContacts) {
      const ch = nameOf(c).trim().charAt(0).toUpperCase();
      if (ch) s.add(ch);
    }
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabContacts, branches]);

  const visible = useMemo(() => {
    let list = tabContacts;
    if (hideDeactivated) list = list.filter((c) => !c.deactivated);
    if (letter) list = list.filter((c) => nameOf(c).trim().toUpperCase().startsWith(letter));
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((c) =>
        nameOf(c).toLowerCase().includes(needle) ||
        (c.city || "").toLowerCase().includes(needle) ||
        (c.phone || "").toLowerCase().includes(needle)
      );
    }
    const sorted = [...list].sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
    if (sort === "za") sorted.reverse();
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabContacts, hideDeactivated, letter, q, sort, branches]);

  // Selected contact is derived via .find() on every render (against the FULL contacts list,
  // not the tab-scoped one) — a delete, a stale id, or a tab switch naturally resolves to null.
  const selected = contacts.find((c) => c.id === selectedId) || null;

  // Every transaction referencing this contact, unfiltered — kept separate from the
  // date-filtered view below so the two empty states (batch 2c requirement) can tell "no
  // transactions ever" apart from "no transactions in this range." **Section F batch 4**: for an
  // INTERNAL contact this is no longer a plain `contactId` lookup — `internalContactTxns` (above)
  // collects by what the contact REPRESENTS, so a Pusat-list branch stand-in now shows that
  // branch's transfers in BOTH directions (not just the ones Pusat sent it), and the single
  // CABANG-list "Pusat" stand-in shows every transfer, any branch, any direction (not just the
  // ones sent TO Pusat). A normal contact is unaffected — it still reads straight off
  // `transactionsByContact`.
  const selectedAllTxns = useMemo(
    () => (selected
      ? (selected.internal ? internalContactTxns(transactions, selected) : (transactionsByContact.get(selected.id) || []))
      : []),
    [selected, transactionsByContact, transactions]
  );

  // ── Business linking (batch 2d) ─────────────────────────────────────────────────────────
  // Every member INCLUDING the one being viewed — for display only (the outstanding total above
  // still sums `businessSiblings`, which deliberately excludes `selected` so it isn't added to
  // itself). Fixes a real gap: the list used to show only the OTHER members, so a reader standing
  // inside a 7-entry firm had no way to see it's 7 without counting siblings and adding one.
  const businessAllMembers = useMemo(
    () => (selected?.businessId ? contactsForBusiness({ contacts }, selected.businessId) : []),
    [selected, contacts]
  );
  // Never fabricated when the business record is missing (root doc's "never fabricate a value
  // you can't truthfully reconstruct" rule, same precedent as `resolveBranchName`) — a plainly
  // labeled fallback instead of silently hiding the section or guessing a name.
  const business = useMemo(
    () => (selected?.businessId ? (businesses.find((b) => b.id === selected.businessId) || null) : null),
    [selected, businesses]
  );
  const businessLabel = selected?.businessId ? (business ? displayBusiness(business) : "(bisnis tidak ditemukan)") : null;
  // "This entry's own" vs "the whole firm's" outstanding — both computed via
  // `utils/piutangUtang.js`'s `outstanding()` (direction-aware: Utang/Piutang/Nett, never one
  // blended figure) so the two totals can never disagree in method, only in scope; both rendered
  // with an unmistakable label so a reader can't mistake the firm total for this one entry's own
  // debt (task requirement). Deactivated sibling entries still count (root doc: deactivation
  // never hides a contact's history/transactions) — `outstandingForBusiness` reads `businesses`-
  // linked `contactId`s straight off `transactions`, same as before.
  // **Gated on `!selected.internal`, matching the Bisnis card's own gate below (the only place
  // either value is ever read) — a real crash found while wiring in `outstanding()`'s TRANSFER
  // guard**: an internal contact's own transactions ARE Belum Lunas TRANSFERs by construction
  // (`resolveTransferContact`, App.js), so calling `outstandingForContact` unconditionally for
  // WHATEVER contact happens to be selected — as this used to do — hit the guard's own throw the
  // instant staff opened the shared "Pusat" internal contact's detail pane, which has real
  // TRANSFER history. Never actually needed there: the Bisnis card that reads these two values
  // never renders for an internal contact in the first place.
  const selfOutstanding = useMemo(
    () => (selected && !selected.internal ? outstandingForContact(transactions, selected.id) : null),
    [transactions, selected]
  );
  const businessOutstanding = useMemo(
    () => (selected && !selected.internal && selected.businessId ? outstandingForBusiness(transactions, contacts, selected.businessId) : null),
    [selected, transactions, contacts]
  );

  // Every DISTINCT branchId this contact's own (unfiltered) transactions actually carry — the
  // data-driven answer to "does this contact's list mix several branches." Computed off
  // `selectedAllTxns`, not the date-filtered view, so narrowing the date range can never make
  // the branch filter itself flicker in/out — it reflects the contact's own nature, not the
  // current view. `showBranchFilter` requires BOTH `selected.internal` (never offered on a
  // normal contact, per instruction, regardless of whether its own data happens to span more
  // than one branch — a Cabang-list shop CAN in principle be transacted with by salespeople from
  // different branches, since `Contact.list` doesn't pin a shop to one branch; this control is
  // withheld from it anyway, deliberately, not because the data can't vary) AND more than one
  // distinct branch actually present — which in practice is true for exactly one contact in this
  // app: the single, shared CABANG-list "Pusat" stand-in (see the header comment above and
  // App.js's own `resolveTransferContact`). A PUSAT-list per-branch stand-in's own transactions
  // only ever carry ONE branchId, so it never qualifies — offering a filter there would be
  // exactly the "control that never changes anything" this was withheld from a normal contact for.
  const selectedBranchIds = useMemo(() => {
    const s = new Set();
    for (const t of selectedAllTxns) if (t.branchId) s.add(t.branchId);
    return s;
  }, [selectedAllTxns]);
  const showBranchFilter = !!selected?.internal && selectedBranchIds.size > 1;

  // The due-within filter's own resolved day-count (batch 2c-due-fix) — the ONE place "semua" /
  // a fixed preset / "kustom" all collapse to either a real `n` or `null` ("apply no due-based
  // filtering at all"), so every other reader (the filter chain, the summary line, the empty-
  // state message, the Lunas-exclusion hint) asks this ONE question instead of re-deriving the
  // preset/custom-text logic each its own way. Delegates to `utils/txnStatus.js`'s
  // `dueWithinActiveN` (Bisnis page work, batch 3 extraction) — this page no longer decides the
  // ternary itself.
  const dueWithinActiveN = resolveDueWithinActiveN(txnDueWithin, txnDueCustomText);
  // The label naming the CURRENT due-within selection, used both in "Menampilkan: …" and next to
  // the Kustom input itself. Delegates to `utils/txnStatus.js`'s `dueWithinSummaryLabel` (same
  // extraction) — see that function's own comment for the `0` → "Hari Ini Saja" rule.
  const dueWithinSummaryLabel = () => resolveDueWithinSummaryLabel(txnDueWithin, dueWithinActiveN);

  const selectedFilteredTxns = useMemo(() => {
    let list = selectedAllTxns;
    // Plain string comparison — see the module-level comment above `fmtLocalDate` for why this
    // must NOT go through an ISO re-parse the way RiwayatTransformasi's filter does.
    if (txnFromDate) list = list.filter((t) => t.date >= txnFromDate);
    if (txnToDate) list = list.filter((t) => t.date <= txnToDate);
    // Combines with the date range (not an alternative to it) — "MMC, Bulan Ini" is one
    // selection. Guarded on `showBranchFilter` so a stale non-"SEMUA" value from a PREVIOUSLY
    // selected contact can never silently apply to a contact whose own control isn't shown.
    if (showBranchFilter && txnBranchFilter !== "SEMUA") list = list.filter((t) => t.branchId === txnBranchFilter);
    // Outstanding-only and due-within (batch 2c-due) — each its own independent AND clause, same
    // "combines, never replaces" discipline the branch filter above already follows. Due-within
    // structurally excludes a LUNAS transaction (dueState always answers 'none' for one,
    // regardless of outstanding-only's own setting) and excludes 'overdue' on purpose — see
    // dueWithinMatches' own header comment. `dueWithinActiveN == null` (semua, or kustom with
    // nothing valid typed yet) means "apply no due-based filtering at all" — never "filter to
    // nothing."
    // No perspective needed here (section F batch 4) — LUNAS is decided by `txnSisa(t) === 0`
    // alone, the same regardless of which side's books `txnStatus` is asked from; only the
    // PIUTANG/UTANG answer (renderTxnRow's own StatusPill, below) depends on it.
    if (txnOutstandingOnly) list = list.filter((t) => txnStatus(t) !== "LUNAS");
    if (dueWithinActiveN != null) list = list.filter((t) => dueWithinMatches(dueState(t, today()), dueWithinActiveN));
    return sortTxnsDesc(list);
  }, [selectedAllTxns, txnFromDate, txnToDate, showBranchFilter, txnBranchFilter, txnOutstandingOnly, dueWithinActiveN]);

  // The overdue banner's own count — deliberately built off `selectedAllTxns`, NOT
  // `selectedFilteredTxns`: "the contact's overdue total, full stop" (task requirement) means
  // every one of this filter above — date range, branch, outstanding-only, due-within — is
  // irrelevant to this number. `dueState` already answers 'none' for a LUNAS transaction
  // regardless of `dueDate`, so "outstanding" is automatic here; no second txnStatus check is
  // layered on top of it.
  const selectedOverdueTxns = useMemo(
    () => sortTxnsDesc(selectedAllTxns.filter((t) => dueState(t, today()).kind === "overdue")),
    [selectedAllTxns]
  );
  // Id lookup for the "⚠ juga di atas" marker (batch 2c-due-fix) — a row can legitimately sit in
  // BOTH the overdue block and the filtered table at once (due-within "Semua" applies no
  // due-based filtering, so an overdue row hasn't failed anything). The marker only disambiguates
  // that overlap for a human reading the table; it never changes which rows either list shows.
  const selectedOverdueIds = useMemo(() => new Set(selectedOverdueTxns.map((t) => t.id)), [selectedOverdueTxns]);

  // The filtered list's own Utang/Piutang/Nett total (batch 2c-due) — `outstanding()` itself
  // (utils/piutangUtang.js), not a second summing method: `selectedFilteredTxns` is already
  // scoped to one contact by construction (built from `selectedAllTxns`), so this is the exact
  // same shape `outstandingForContact` computes, just over an already-narrowed list.
  // **Gated on `!selected.internal`, same reason/precedent as `selfOutstanding` above**:
  // `outstanding()` throws on a Belum Lunas TRANSFER, and an internal contact's own transactions
  // (the shared "Pusat" stand-in, a branch's own Pusat-list stand-in) are exactly that by
  // construction — calling it unconditionally here would crash the page the instant one of them
  // has an unpaid transfer in view.
  const selectedFilteredTotal = useMemo(
    () => (selected && !selected.internal ? outstanding(selectedFilteredTxns) : null),
    [selected, selectedFilteredTxns]
  );

  const txnRangeLabel = useMemo(() => {
    const presetName = txnPreset ? TXN_PRESET_LABEL[txnPreset] : null;
    if (!txnFromDate && !txnToDate) return presetName || "Semua";
    const range = txnFromDate === txnToDate
      ? fmtDateShort(txnFromDate)
      : `${fmtDateShort(txnFromDate)} – ${fmtDateShort(txnToDate)}`;
    return presetName ? `${presetName} · ${range}` : range;
  }, [txnPreset, txnFromDate, txnToDate]);
  const txnBranchLabel = txnBranchFilter === "SEMUA" ? "Semua Cabang" : (resolveBranchName(branches, txnBranchFilter) || "Cabang");
  // Whatever is selected must be visible the same way the date range is — when the branch
  // filter is offered at all, it's named EVERY time, including its own default ("Semua Cabang"),
  // exactly like "Bulan Ini" is named even though it's the date filter's own default. Extended
  // (batch 2c-due) to name outstanding-only/due-within too — but only while ACTIVE (non-default),
  // unlike the date range/branch pieces above: those two have no genuine "off" state (there's
  // always SOME range, and the branch control is only ever rendered when it's meaningful), while
  // outstanding-only/due-within genuinely can be off, and naming "Semua Status"/"Jatuh Tempo:
  // Semua" every time would just be noise on the page's normal, unfiltered state.
  // Not memoized (unlike `txnRangeLabel` above) — a plain string join this cheap doesn't need it,
  // and `dueWithinSummaryLabel` is itself a fresh closure every render, so memoizing around it
  // would buy nothing. Only names due-within while it's ACTUALLY applied (`dueWithinActiveN !=
  // null`) — "kustom" with nothing valid typed yet applies no filtering, so it stays unnamed here
  // too (the dedicated "belum diterapkan" hint under the row is what explains that state).
  const txnFilterSummaryParts = [];
  if (txnOutstandingOnly) txnFilterSummaryParts.push("Hanya Belum Lunas");
  const dueWithinLabel = dueWithinSummaryLabel();
  if (dueWithinLabel) txnFilterSummaryParts.push(dueWithinLabel);
  if (showBranchFilter) txnFilterSummaryParts.push(txnBranchLabel);
  txnFilterSummaryParts.push(txnRangeLabel);
  const txnFilterSummary = txnFilterSummaryParts.join(" · ");

  // Live-derived by id (not a stored object), same pattern Penjualan.js/Pembelian.js use for
  // their own detail modal — a stale id (contact switched, transaction deleted) resolves to null.
  const detailTxn = detailTxnId ? transactions.find((t) => t.id === detailTxnId) || null : null;

  // The shared reference-count rule (utils/contacts.js's `contactRefCount`) — this page used to
  // hand-roll the identical "history entries + transactions" sum locally, a second copy of the
  // same rule that had to be kept in sync by hand. Delegates to the real function instead, still
  // memoized per selected contact so it doesn't re-scan the full history/transactions arrays on
  // every keystroke elsewhere on the page (search, sort, tab switch, ...).
  const selectedRefCount = useMemo(
    () => (selected ? contactRefCount({ history, transactions }, selected.id) : 0),
    [selected, history, transactions]
  );
  // Internal contacts are NEVER deletable, regardless of reference count — `deleteContact`
  // (utils/contacts.js) refuses the write outright for one. The OLD guard here only ever
  // checked the reference count, so an UNREFERENCED internal contact's Hapus button looked
  // enabled and silently did nothing when clicked (the write path refused, but nothing on
  // screen said why, or even that anything had been refused). This is that fix — the page's own
  // check now knows about `internal` too, with its own distinct reason shown.
  const canDelete = selected ? (!selected.internal && selectedRefCount === 0) : false;
  const deleteBlockedReason = !selected
    ? ""
    : selected.internal
    ? "Kontak internal tidak bisa dihapus."
    : selectedRefCount > 0
    ? `Dipakai di ${selectedRefCount} riwayat/transaksi — tidak bisa dihapus.`
    : "";

  const openCreate = () => { setSaveError(null); setFormModal({ initial: null }); };
  const openEdit = (c) => { setSaveError(null); setFormModal({ initial: c }); };

  // `onAdd`/`onUpdate` (App.js's handleAddContact/handleUpdateContact) return `{ok, error}` — a
  // failed save (a name collision, a since-deleted person/business, an internal contact) shows
  // the real reason and keeps the form open, instead of a silent close-with-nothing-changed.
  const saveContact = (fields) => {
    const isCreate = !formModal.initial;
    const result = isCreate ? onAdd(fields) : onUpdate(formModal.initial.id, fields);
    if (result && result.ok === false) {
      setSaveError(result.error || "Kontak tidak bisa disimpan.");
      return;
    }
    setSaveError(null);
    setFormModal(null);
    // Suggest possible-same-firm candidates only right after a BY-HAND create (never on edit —
    // an edited contact isn't a "new entry that might duplicate an existing firm"). `contacts`
    // here is still the PRE-create array for this event-handler tick (the real write only lands
    // on the next render, same staleness `ContactPicker`'s own `freshContact` fallback works
    // around) — which is exactly the right candidate set: every contact that already existed
    // before this one, so the new contact can never match itself. "If nothing is found, show
    // NOTHING" (task spec) is simply never setting `suggestFor` at all.
    if (isCreate && result && result.contact) {
      const matches = suggestBusinessMatches(result.contact, contacts);
      if (matches.length > 0) setSuggestFor({ contact: result.contact, matches });
    }
  };

  // Business linking (batch 2d). `onLinkContacts` (App.js's `handleLinkContacts`) may also mint
  // a brand-new Business record (when `payload.businessName` is given) — this page never creates
  // one itself. On success, close whichever surface triggered it (the "+ Tautkan ke Bisnis"
  // picker AND, if this came from the suggestion banner, the banner too — a completed link counts
  // as "acted on," see the banner's own comment below). On failure, keep the picker open and show
  // the real reason instead of a silent no-op — same contract `saveContact` above already uses.
  const confirmLink = (payload) => {
    const result = onLinkContacts(payload);
    if (result && result.ok === false) {
      setLinkError(result.error || "Kontak tidak bisa ditautkan.");
      return;
    }
    setLinkError(null);
    setLinkModal(null);
    setSuggestFor(null);
  };

  // Contact.personIds are SALESPERSON PERSON ids (never role ids, see utils/contacts.js) — the
  // "linked salespeople" a contact carries resolve by NAME straight from the person, no
  // `salespeople` collection needed here at all.
  const personLabel = (personId) => {
    const name = resolvePersonName(persons, personId);
    if (!name) return null; // unresolved (should not happen — deletePerson is blocked while any Contact.personIds still links to it)
    const code = resolvePersonCode(persons, personId);
    return code ? `${name} (${code})` : name;
  };

  // Shared row markup between the always-visible overdue block and the main filtered table below
  // (batch 2c-due) — the same 7 columns, so the two can never drift out of sync with each other.
  // A plain closure (not a separate component) so it captures `branches`/`setDetailTxnId` for
  // free, same convention the rest of this render function already uses.
  // `markDuplicate` (batch 2c-due-fix): the MAIN table passes `true` so a row that's ALSO sitting
  // in the (open) overdue block above gets a small pointer — the two lists can legitimately show
  // the same row at once (see `selectedOverdueIds`'s own comment), and without this a reader can
  // mistake that for two different transactions. The overdue block's OWN rows never pass this —
  // pointing a row at itself inside the very block it's already in would be meaningless.
  const renderTxnRow = (t, { markDuplicate = false } = {}) => {
    const invoiceNo = (t.type === "PEMBELIAN" ? t.supplierInvoiceNo : t.invoiceNumber) || "—";
    // Same expression Penjualan.js's own LOKASI badge uses — see the main table's own comment
    // below, kept there too since this function is deliberately unexported/page-local.
    const branchName = t.location === "CABANG" ? resolveBranchName(branches, t.branchId) : null;
    const due = dueState(t, today());
    // Gated on `showOverdueBlock` too — "only when the block is actually open; when it's
    // collapsed there is nothing above to point at" (task requirement).
    const isDuplicateOfOverdueBlock = markDuplicate && showOverdueBlock && selectedOverdueIds.has(t.id);
    return (
      <tr key={t.id} onClick={() => setDetailTxnId(t.id)}>
        <td className="date-cell">
          {fmtDateShort(t.date)}<br />
          <span style={{ color: "var(--muted)", fontSize: 12 }}>{t.time}</span>
        </td>
        <td className="mono">{invoiceNo}</td>
        {/* Section F, batch 5: a TRANSFER row's direction is otherwise invisible on an internal
            contact's page, which (since batch 4) can legitimately mix both directions on one
            contact — the Lokasi badge alone requires already knowing it means the source. Folded
            into Jenis (never a new column — the table stays at its current width) since "Transfer"
            already lives here; a muted sub-line reuses `transferDirectionLabel`
            (`utils/piutangUtang.js`) VERBATIM — the exact "{source} → {destination}" string
            Piutang & Utang's own Internal tab already shows, never a second phrasing. Never
            rendered for PENJUALAN/PEMBELIAN — an ordinary row has no direction and looks exactly
            as it did before this batch. */}
        <td>
          {TXN_TYPE_LABEL[t.type] || t.type}
          {t.type === "TRANSFER" && (
            <div className="kontak-transfer-direction form-hint" style={{ fontSize: 11, marginTop: "var(--gap-xs)" }}>
              {transferDirectionLabel(t, branches)}
            </div>
          )}
        </td>
        <td><span className="chip chip--muted">{branchName || bucketLabel(t.location)}</span></td>
        <TxnNilaiCell txn={t} />
        {/* Section F batch 4: perspective threaded through for an internal contact, so a
            transfer reads PIUTANG/UTANG from THIS contact's own side (never the old
            always-PIUTANG whole-business default) — see `internalPerspectiveFor`'s own
            comment above `sortTxnsDesc`. A no-op for every normal contact's PENJUALAN/
            PEMBELIAN rows (`internalPerspectiveFor` returns `undefined` for those). */}
        <td><StatusPill status={txnStatus(t, internalPerspectiveFor(selected, t))} /></td>
        <td className="due-cell">
          {due.kind === "none" ? "—" : <DueBadge due={due} />}
          {isDuplicateOfOverdueBlock && (
            <div className="kontak-duplicate-marker form-hint" style={{ fontSize: 11, marginTop: "var(--gap-xs)" }}>
              ⚠ juga di atas
            </div>
          )}
        </td>
      </tr>
    );
  };
  const txnTableHead = (
    <thead>
      <tr>
        <th>Tanggal &amp; Waktu</th><th>No. Invoice</th><th>Jenis</th>
        <th>Lokasi</th><th className="num">Total Nilai</th><th>Status</th><th className="due-cell">Jatuh Tempo</th>
      </tr>
    </thead>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Kontak</h1>
          <div className="page-sub">{tabContacts.length} kontak</div>
        </div>
        <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
          <button className="btn" onClick={onOpenBisnis}>Bisnis</button>
          <button className="btn" onClick={onImportKontak}>Impor Kontak</button>
          {/* Section A: every real contact, BOTH lists, whatever tab/search is showing — see
              utils/contactExport.js (re-importable PUSAT/CABANG sheets + a full detail sheet). */}
          <button className="btn" onClick={() => downloadContactExport({ contacts, persons, businesses }, today())}>Ekspor Kontak</button>
          <button className="btn btn-primary" onClick={openCreate}>+ Tambah Kontak</button>
        </div>
      </div>

      {/* Suggested-match banner (batch 2d) — appears only right after a hand-created contact
          finds at least one loose candidate (utils/contactMatch.js). A persistent card the user
          must act on (Tautkan) or dismiss (Abaikan), never a fading toast — it stays until one
          of those happens, independent of any tab/selection navigation elsewhere on the page. */}
      {suggestFor && (
        <div className="kontak-suggest">
          <div className="card__title" style={{ marginBottom: "var(--gap-sm)" }}>
            Kemungkinan bisnis yang sama dengan "{suggestFor.contact.name}"
          </div>
          <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>
            Kontak baru ini mirip dengan kontak lain yang sudah ada. Ini hanya saran — tautkan
            hanya jika Anda yakin ini perusahaan/toko yang sama.
          </div>
          {suggestFor.matches.map(({ contact: cand, reasons }) => (
            <div key={cand.id} className="kontak-suggest__row">
              <div className="kontak-suggest__row-main">
                <div className="kontak-suggest__row-name">
                  {cand.name}
                  <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>{CONTACT_LIST_LABEL[cand.list] || cand.list}</span>
                </div>
                <div className="kontak-suggest__row-reason">{reasons.join(" · ")}</div>
              </div>
              <button type="button" className="btn btn-sm"
                onClick={() => setLinkModal(
                  // A suggested candidate already in some (other) business can no longer be
                  // ticked directly in the picker's own KONTAK section (a business-having
                  // contact is shown there but unpickable) — preselect its FIRM in the BISNIS
                  // section instead, so the checkbox that opens still corresponds to a real,
                  // pickable, visible row.
                  cand.businessId
                    ? { contact: suggestFor.contact, preselectContactIds: [], preselectBusinessIds: [cand.businessId] }
                    : { contact: suggestFor.contact, preselectContactIds: [cand.id], preselectBusinessIds: [] }
                )}>
                Tautkan
              </button>
            </div>
          ))}
          <div className="modal-actions" style={{ justifyContent: "flex-start", marginTop: "var(--gap-md)" }}>
            <button type="button" className="btn" onClick={() => setSuggestFor(null)}>Abaikan</button>
          </div>
        </div>
      )}

      {/* Browsing pane — tabs, toolbar, A-Z index, list — always mounted, hidden via CSS (not
          unmounted) the instant a contact is selected, so its DOM state (list scroll position,
          search text, sort, active letter, active tab) survives a round trip to the detail pane
          and back. See `.kontak-browse--hidden` in styles.css for why this is a new class rather
          than a modifier on `.kontak-layout`/`.kontak-list` (BisnisPage reuses those verbatim). */}
      <div className={"kontak-browse" + (selected ? " kontak-browse--hidden" : "")}>
        <div className="seg" style={{ marginBottom: "var(--gap-lg)" }}>
          <button type="button" className={tab === "PUSAT" ? "active" : ""} onClick={() => changeTab("PUSAT")}>Pusat</button>
          <button type="button" className={tab === "CABANG" ? "active" : ""} onClick={() => changeTab("CABANG")}>Cabang</button>
        </div>

        <div className="toolbar">
          <input className="form-input grow" placeholder="Cari nama, kota, atau telepon…"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="form-select" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="az">Nama A–Z</option>
            <option value="za">Nama Z–A</option>
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)", whiteSpace: "nowrap", fontSize: 13 }}>
            <input type="checkbox" checked={hideDeactivated}
              onChange={(e) => setHideDeactivated(e.target.checked)} />
            Sembunyikan yang nonaktif
          </label>
        </div>

        <div className="kontak-azrow">
          <button type="button" className={letter === null ? "active" : ""} onClick={() => setLetter(null)}>Semua</button>
          {ALPHABET.map((l) => (
            <button type="button" key={l}
              className={letter === l ? "active" : ""}
              disabled={!availableLetters.has(l)}
              onClick={() => setLetter(l)}>
              {l}
            </button>
          ))}
        </div>

        <div className="kontak-list card">
          {visible.length === 0 ? (
            <div className="empty-state">
              {tabContacts.length === 0
                ? `Belum ada kontak di daftar ${tab === "PUSAT" ? "Pusat" : "Cabang"}.`
                : "Tidak ada kontak cocok."}
            </div>
          ) : (
            visible.map((c) => {
              // Section F batch 4: an internal contact's row count reads the same EXPANDED
              // (buyer+seller) collector the detail pane uses, so the two can never disagree —
              // see `internalTxnCounts`'s own comment above.
              const count = c.internal ? (internalTxnCounts.get(c.id) || 0) : (transactionsByContact.get(c.id)?.length || 0);
              return (
                <div key={c.id}
                  className={"picker__row" + (c.id === selectedId ? " kontak-row--active" : "")}
                  onClick={() => setSelectedId(c.id)}>
                  <div className="picker__name">
                    {nameOf(c)}
                    {c.city && <span style={{ color: "var(--muted)", fontWeight: 400 }}> — {c.city}</span>}
                    {c.internal && <span className="chip chip--internal" style={{ marginLeft: "var(--gap-sm)" }}>Internal</span>}
                    {c.deactivated && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>Nonaktif</span>}
                  </div>
                  <div className="picker__meta">{count} transaksi</div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* No contact selected: browsing pane above is the whole page, full width — nothing else
          renders. Selecting one hides the browsing pane (CSS, above) and this pane takes the
          full width in its place; "← Kembali" clears `selected`, which un-hides it. */}
      {selected && (
        <div className="kontak-detail">
          <button type="button" className="btn" style={{ marginBottom: "var(--gap-lg)" }}
            onClick={() => setSelectedId(null)}>
            ← Kembali
          </button>

              <div className="card">
                <h2 className="modal-title" style={{ marginBottom: "var(--gap-lg)" }}>
                  {nameOf(selected)}
                  {selected.internal && <span className="chip chip--internal" style={{ marginLeft: "var(--gap-md)" }}>Internal</span>}
                  {selected.deactivated && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-md)" }}>Nonaktif</span>}
                </h2>
                <dl className="detail-grid">
                  <dt>Alamat</dt>
                  <dd>
                    {selected.addresses && selected.addresses.length > 0 ? (
                      <>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {selected.addresses.map((a) => (
                            <li key={a.id}>
                              {a.text || "—"}
                              {a.isDefault && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>Default</span>}
                            </li>
                          ))}
                        </ul>
                        {!selected.addresses.some((a) => a.isDefault) && (
                          <div className="form-hint" style={{ marginTop: "var(--gap-xs)" }}>Tidak ada alamat default.</div>
                        )}
                      </>
                    ) : "—"}
                  </dd>
                  <dt>Ekspedisi</dt>
                  <dd>{selected.ekspedisi && selected.ekspedisi.length > 0 ? selected.ekspedisi.join(", ") : "—"}</dd>
                  <dt>Kota</dt><dd>{selected.city || "—"}</dd>
                  <dt>Telepon</dt><dd>{selected.phone || "—"}</dd>
                  <dt>Contact Person</dt><dd>{selected.contactPerson || "—"}</dd>
                  <dt>Email</dt><dd>{selected.email || "—"}</dd>
                  <dt>Keterangan</dt><dd>{selected.keterangan || "—"}</dd>
                  <dt>NPWP</dt><dd>{selected.npwp || "—"}</dd>
                  <dt>PKP</dt><dd>{selected.pkp ? "Ya" : "Tidak"}</dd>
                  <dt>Label</dt>
                  <dd>
                    {selected.labels && selected.labels.length > 0
                      ? selected.labels.map((l) => LABEL_TEXT[l] || l).join(", ")
                      : "—"}
                  </dd>
                  <dt>Sales / Person</dt>
                  <dd>
                    {selected.personIds && selected.personIds.length > 0
                      ? (selected.personIds.map(personLabel).filter(Boolean).join(", ") || "—")
                      : "—"}
                  </dd>
                </dl>
                <div className="modal-actions" style={{ justifyContent: "flex-start", marginTop: "var(--gap-lg)" }}>
                  {selected.internal ? (
                    <span className="tip" data-tip="Kontak internal tidak bisa diubah di sini.">
                      <button type="button" className="btn" disabled>Ubah</button>
                    </span>
                  ) : (
                    <button type="button" className="btn" onClick={() => openEdit(selected)}>Ubah</button>
                  )}
                  {canDelete ? (
                    <button type="button" className="btn kontak-btn-danger" onClick={() => setDeleteTarget(selected)}>Hapus</button>
                  ) : (
                    <span className="tip" data-tip={deleteBlockedReason}>
                      <button type="button" className="btn kontak-btn-danger" disabled>Hapus</button>
                    </span>
                  )}
                </div>
              </div>

              {/* Business linking (batch 2d, restructured batch 2f) — never shown for an
                  internal (app-maintained TRANSFER stand-in) contact; it can never be the same
                  real firm as anything. */}
              {!selected.internal && (
                <div className="card">
                  <div className="card__title">Bisnis</div>
                  {selected.businessId ? (
                    <div>
                      {/* The firm's own name is the heading of this card's real content — NOT a
                          value sitting inline next to a button; the detach action is de-emphasized
                          (btn-ghost, same visual weight as a sibling's own "Lepas") so it never
                          competes with the name for attention. */}
                      <div className="kontak-biz__header">
                        <h3 className="kontak-biz__name">{businessLabel}</h3>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onUpdate(selected.id, { businessId: null })}>
                          Lepas {nameOf(selected)} dari bisnis
                        </button>
                      </div>

                      {businessAllMembers.length > 0 && (
                        <>
                          {/* Covers ALL members, including the one being viewed — a real gap
                              found in review: the old "Entitas lain dalam bisnis ini" heading
                              only ever listed the OTHER members, so a reader standing inside a
                              7-entry firm had no on-screen way to see it's 7 at all. */}
                          <div className="kontak-section-label">Entitas dalam bisnis ini ({businessAllMembers.length} entri)</div>
                          {/* A real bounded box (border + rounded corners) so the member rows
                              read as ONE LIST, not floating rows loose on the card. */}
                          <div className="kontak-biz__member-list">
                            {businessAllMembers.map((c) => {
                              const isSelf = c.id === selected.id;
                              return (
                                <div key={c.id} className={isSelf ? "picker__row picker__row--static" : "picker__row"}
                                  onClick={isSelf ? undefined : () => jumpToContact(c)}>
                                  <div className="picker__name">
                                    {nameOf(c)}
                                    <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>{CONTACT_LIST_LABEL[c.list] || c.list}</span>
                                    {c.deactivated && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>Nonaktif</span>}
                                    {isSelf && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>(kontak ini)</span>}
                                  </div>
                                  {/* Self gets no Lepas button here — the detach-this-contact
                                      control already sits next to the firm name above; a second
                                      one on this row would be the same action twice. */}
                                  {!isSelf && (
                                    <button type="button" className="btn btn-ghost btn-sm"
                                      onClick={(e) => { e.stopPropagation(); onUpdate(c.id, { businessId: null }); }}>
                                      Lepas {nameOf(c)} dari bisnis
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}

                      {/* Two distinctly TINTED tiles (mirrors BayarTagihanModal's own "Sisa
                          Barang"/"Sisa Tagihan & Potongan" precedent), each computed via the SAME
                          `outstanding()` shape so the two totals can never disagree in method,
                          only in scope. Each tile shows all THREE lines — Utang, Piutang, Nett
                          (labelled with its own direction) — always, even when a figure is 0:
                          "nothing outstanding" (utang=0, piutang=0) and "two debts that cancel
                          out" (utang===piutang>0, nett=0) must never render identically. */}
                      <div className="kontak-biz__totals">
                        <div className="kontak-biz__total kontak-biz__total--self">
                          <div className="kontak-biz__total-label">Outstanding (kontak ini)</div>
                          <div className="kontak-biz__total-lines">
                            <div className="kontak-biz__total-line">Utang: Rp {fmtQty(selfOutstanding.utang)}</div>
                            <div className="kontak-biz__total-line">Piutang: Rp {fmtQty(selfOutstanding.piutang)}</div>
                            <div className="kontak-biz__total-value">{nettLabel(selfOutstanding)}: Rp {fmtQty(Math.abs(selfOutstanding.nett))}</div>
                          </div>
                        </div>
                        <div className="kontak-biz__total kontak-biz__total--firm">
                          <div className="kontak-biz__total-label">Outstanding (seluruh bisnis)</div>
                          <div className="kontak-biz__total-lines">
                            <div className="kontak-biz__total-line">Utang: Rp {fmtQty(businessOutstanding.utang)}</div>
                            <div className="kontak-biz__total-line">Piutang: Rp {fmtQty(businessOutstanding.piutang)}</div>
                            <div className="kontak-biz__total-value">{nettLabel(businessOutstanding)}: Rp {fmtQty(Math.abs(businessOutstanding.nett))}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>Belum tertaut ke bisnis manapun.</div>
                  )}
                  <button type="button" className="btn btn-sm" style={{ marginTop: "var(--gap-md)" }}
                    onClick={() => { setLinkError(null); setLinkModal({ contact: selected, preselectContactIds: [], preselectBusinessIds: [] }); }}>
                    + Tautkan ke Bisnis
                  </button>
                </div>
              )}

              <div className="card">
                <div className="card__title">Riwayat Transaksi ({selectedFilteredTxns.length})</div>

                {/* Overdue disclosure (batch 2c-due) — ALWAYS visible whenever this contact has
                    any overdue transaction, regardless of every filter below (date range, branch,
                    outstanding-only, due-within all included) — "the contact's overdue total,
                    full stop." Collapsed by default; clicking reveals those rows in their OWN
                    block, ABOVE and visually separate from the filtered table, which stays
                    exactly as its own filters left it — a row that fails the filtered table's own
                    filters must never silently sit inside it. Layout batch C: moved to be the
                    FIRST thing under the card title, ahead of even the total tile — it's the one
                    piece of chrome on this page that deliberately can't be filtered away, and
                    must stay the first thing seen. Its own hint line is folded INSIDE the banner
                    (one visual unit, not two) and the expandable block sits flush under it (no
                    gap) so open-or-closed this still reads as one component. */}
                {selectedOverdueTxns.length > 0 && (
                  <div className="kontak-overdue-wrap">
                    <div
                      className="kontak-overdue-banner"
                      role="button" tabIndex={0}
                      onClick={() => setShowOverdueBlock((v) => !v)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowOverdueBlock((v) => !v); }}>
                      <div className="kontak-overdue-banner__text">
                        ⚠ {selectedOverdueTxns.length} transaksi sudah lewat jatuh tempo — klik untuk {showOverdueBlock ? "sembunyikan" : "lihat"}
                      </div>
                      <div className="kontak-overdue-hint">
                        Semua transaksi lewat jatuh tempo kontak ini, di luar filter apa pun.
                      </div>
                    </div>
                    {showOverdueBlock && (
                      <div className="kontak-overdue-block">
                        <div className="kontak-overdue-block__title">
                          Transaksi Lewat Jatuh Tempo ({selectedOverdueTxns.length})
                        </div>
                        <div className="table-wrap">
                          <table className="table">
                            {txnTableHead}
                            <tbody>{selectedOverdueTxns.map(renderTxnRow)}</tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* The filtered list's own Utang/Piutang/Nett total (batch 2c-due) — reuses the
                    exact three-line tile the Bisnis card above already renders, so the two can
                    never disagree in shape, only in scope. Gated on `!selected.internal` — see
                    `selectedFilteredTotal`'s own comment for why. Shown even when the filtered
                    list is empty (Rp 0/Rp 0/Nett Rp 0) — never silently hidden, same "always
                    three lines" rule the Bisnis card already follows. Layout batch C: moved up to
                    sit right after the overdue banner, ahead of every filter — "here is the state
                    of this contact" before the controls that narrow it; its position in the JSX
                    has no bearing on what it computes (unchanged from batch B otherwise). */}
                {selectedFilteredTotal && (
                  <div className="kontak-biz__total kontak-biz__total--self kontak-riwayat-total">
                    <div className="kontak-biz__total-label">Total (transaksi yang tampil)</div>
                    <div className="kontak-biz__total-lines">
                      <div className="kontak-biz__total-line">Utang: Rp {fmtQty(selectedFilteredTotal.utang)}</div>
                      <div className="kontak-biz__total-line">Piutang: Rp {fmtQty(selectedFilteredTotal.piutang)}</div>
                      <div className="kontak-biz__total-value">{nettLabel(selectedFilteredTotal)}: Rp {fmtQty(Math.abs(selectedFilteredTotal.nett))}</div>
                    </div>
                  </div>
                )}

                {/* Three filter groups, one row (layout batch C) — grouped by the QUESTION each
                    one answers (when it happened / what state it's in / when it's due), never by
                    control TYPE. One bordered panel with a rule between columns, not three
                    separate boxes — a lone checkbox next to two fuller columns would read as an
                    empty box on its own. */}
                <div className="kontak-riwayat-filters">
                  {/* Tanggal Transaksi — WHEN this transaction happened (t.date), backward-
                      looking: can reach into the past ("Bulan Lalu") or show everything ever
                      ("Semua"). */}
                  <div className="kontak-txn-date-block">
                    <div className="kontak-riwayat-heading">
                      Tanggal Transaksi
                      <span className="form-hint kontak-riwayat-heading__hint">(kapan transaksi ini terjadi)</span>
                    </div>
                    <div className="kontak-txn-date-row">
                      <div className="seg kontak-date-seg">
                        {TXN_DATE_PRESETS.map(([key, label]) => (
                          <button key={key} type="button"
                            className={txnPreset === key ? "active" : ""}
                            onClick={() => chooseTxnPreset(key)}>
                            {label}
                          </button>
                        ))}
                      </div>
                      <div className="kontak-date-range-row">
                        <input type="date" className="form-input kontak-date-input"
                          value={txnFromDate}
                          onChange={(e) => { setTxnRange([e.target.value, txnToDate]); setTxnPreset(""); }} />
                        <span className="kontak-date-range-sep">s/d</span>
                        <input type="date" className="form-input kontak-date-input"
                          value={txnToDate}
                          onChange={(e) => { setTxnRange([txnFromDate, e.target.value]); setTxnPreset(""); }} />
                      </div>
                    </div>
                  </div>

                  {/* Jatuh Tempo — WHEN this transaction is DUE (dueState, off txn.dueDate),
                      forward-looking ONLY, counted from today — deliberately the mirror image of
                      Tanggal Transaksi above, never a second copy of it. Button text itself
                      carries the direction ("N Hari Lagi") so the two columns can't be confused
                      even skimmed. Outstanding-only shares this column (layout batch C) — a real
                      third column for one lone checkbox read as visibly empty next to its two
                      fuller neighbors once "Tanggal Transaksi"/"Jatuh Tempo" needed a second
                      button row at this width (checked in a real browser, not guessed). Both
                      controls are about a transaction's payment STATE (has it been paid vs. when
                      is it due), so folding them together reads as one coherent group rather than
                      an arbitrary merge — independent of each other functionally (unchanged),
                      just sharing a column visually. */}
                  <div className="kontak-txn-due-block">
                    <div className="kontak-riwayat-heading">
                      Jatuh Tempo
                      <span className="form-hint kontak-riwayat-heading__hint">(kapan harus dibayar, dihitung dari hari ini)</span>
                    </div>
                    <label className="kontak-outstanding-toggle">
                      <input type="checkbox" checked={txnOutstandingOnly}
                        onChange={(e) => setTxnOutstandingOnly(e.target.checked)} />
                      Hanya yang Belum Lunas (Outstanding)
                    </label>
                    <div className="seg kontak-due-seg">
                      <button type="button" className={txnDueWithin === "semua" ? "active" : ""}
                        onClick={() => setTxnDueWithin("semua")}>
                        Semua
                      </button>
                      {TXN_DUE_PRESETS.map(([key, label]) => (
                        <button key={key} type="button"
                          className={txnDueWithin === key ? "active" : ""}
                          onClick={() => setTxnDueWithin(key)}>
                          {label}
                        </button>
                      ))}
                      {/* "Kustom" (batch 2c-due-fix) — a typed day-count, mutually exclusive with
                          the three fixed presets above (one `txnDueWithin` value, same as
                          always). Its own inline field renders BELOW this row, not inside it, and
                          ONLY while selected — the row itself never gets wider than four buttons
                          regardless of whether Kustom is active. */}
                      <button type="button" className={txnDueWithin === "kustom" ? "active" : ""}
                        onClick={() => setTxnDueWithin("kustom")}>
                        Kustom
                      </button>
                    </div>
                    {txnDueWithin === "kustom" && (
                      <div className="kontak-due-custom-row">
                        <input type="number" className="form-input kontak-due-custom-input"
                          placeholder="jumlah hari"
                          value={txnDueCustomText}
                          onChange={(e) => setTxnDueCustomText(e.target.value)} />
                        {/* `0` reads as "(hari ini saja)", never "0 hari lagi" — see
                            `dueWithinSummaryLabel`'s own comment for why. */}
                        <span className="form-hint">{dueWithinActiveN === 0 ? "(hari ini saja)" : "hari lagi"}</span>
                      </div>
                    )}
                    {dueWithinActiveN != null ? (
                      <div className="form-hint kontak-due-hint">
                        Transaksi yang sudah Lunas tidak ikut ditampilkan di sini — dan yang sudah
                        lewat jatuh tempo tetap terlihat lewat baris ⚠ di atas, bukan di sini.
                      </div>
                    ) : txnDueWithin === "kustom" ? (
                      // The "must SAY so" requirement — a filter that looks selected but silently
                      // does nothing (blank, negative, or otherwise invalid) is exactly the shape
                      // this whole batch exists to remove.
                      <div className="form-hint kontak-due-custom-unapplied">
                        Belum ada jumlah hari yang valid — filter Jatuh Tempo belum diterapkan,
                        semua transaksi tetap tampil.
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Branch filter — ONLY for the one internal contact whose own transactions
                    actually span more than one branch (see `showBranchFilter`'s own comment
                    above); never shown for a normal contact. Combines with every filter above,
                    not an alternative to any of them — one combined selection. Its own row, not
                    folded into any of the three groups above — it doesn't answer "when"/"what
                    state"/"when due", so forcing it into one of those would misrepresent it. */}
                {showBranchFilter && (
                  <div className="seg kontak-branch-filter">
                    <button type="button" className={txnBranchFilter === "SEMUA" ? "active" : ""}
                      onClick={() => setTxnBranchFilter("SEMUA")}>
                      Semua Cabang
                    </button>
                    {branches.map((b) => (
                      <button key={b.id} type="button"
                        className={txnBranchFilter === b.id ? "active" : ""}
                        onClick={() => setTxnBranchFilter(b.id)}>
                        {b.name}
                      </button>
                    ))}
                  </div>
                )}

                {/* Always visible, regardless of how many rows are showing — a contact can have
                    years of history behind a "Bulan Ini" default, and this must never silently
                    read as "this is everything" (batch 2c requirement). Names every ACTIVE
                    filter — outstanding-only, due-within, branch (batch 2c-due) — alongside the
                    date range, which (unlike those three) is always named even at its own
                    default, since it has no genuine "off" state. Stays immediately above the
                    table it describes (layout batch C). */}
                <div className="form-hint kontak-filter-summary">Menampilkan: {txnFilterSummary}</div>

                {selectedAllTxns.length === 0 ? (
                  <div className="empty-state">Belum ada transaksi dengan kontak ini.</div>
                ) : selectedFilteredTxns.length === 0 ? (
                  <div className="empty-state">Tidak ada transaksi yang cocok dengan filter ini ({txnFilterSummary}).</div>
                ) : (
                  <div className="table-wrap kontak-riwayat-table">
                    <table className="table">
                      {txnTableHead}
                      <tbody>{selectedFilteredTxns.map((t) => renderTxnRow(t, { markDuplicate: true }))}</tbody>
                    </table>
                  </div>
                )}
              </div>
        </div>
      )}

      {formModal && (
        <ContactFormModal
          initial={formModal.initial}
          persons={persons}
          error={saveError}
          onSave={saveContact}
          onClose={() => { setSaveError(null); setFormModal(null); }}
        />
      )}

      {linkModal && (
        <BusinessLinkModal
          mode="link"
          contact={linkModal.contact}
          contacts={contacts}
          businesses={businesses}
          initialSelectedContactIds={linkModal.preselectContactIds}
          initialSelectedBusinessIds={linkModal.preselectBusinessIds}
          error={linkError}
          onConfirm={confirmLink}
          onClose={() => { setLinkError(null); setLinkModal(null); }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title="Hapus kontak?"
          message={<>Anda akan menghapus kontak <b>{nameOf(deleteTarget)}</b>. Tindakan ini permanen.</>}
          requireText="HAPUS"
          onConfirm={() => {
            onDelete(deleteTarget.id);
            if (selectedId === deleteTarget.id) setSelectedId(null);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {detailTxn && (
        <TransactionDetailModal
          txn={detailTxn}
          items={items}
          contacts={contacts}
          salespeople={salespeople}
          persons={persons}
          branches={branches}
          today={today()}
          onClose={() => setDetailTxnId(null)}
        />
      )}
    </div>
  );
}
